/**
 * Admin → Help Center: full CRUD over /api/admin/help/topics.
 * Topics use a slug (kebab-case) for deep-linking and admin-friendly identity.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    LifeBuoy, Plus, Edit3, Trash2, Loader2, ChevronDown, RefreshCw,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
    'Getting Started', 'Deposits', 'Cashing Out', 'Slots', 'Sports',
    'Lottery', 'Free Play & Bonuses', 'Referrals', 'VIP & Achievements', 'Wallet',
];

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const blankTopic = () => ({
    slug: '', title: '', category: CATEGORIES[0], icon: 'HelpCircle',
    short: '', body_md: '', pro_tip: '', sort_order: 0, enabled: true,
});

const HelpAdminTab = () => {
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/help/topics`, { withCredentials: true });
            setTopics(data || []);
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load topics'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!editing) return;
        const t = { ...editing };
        if (!t.slug) t.slug = slugify(t.title);
        if (!t.slug || !t.title || !t.short || !t.body_md) {
            toast.error('Slug, title, short, and body are required');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                slug: t.slug, title: t.title, category: t.category,
                icon: t.icon || 'HelpCircle', short: t.short, body_md: t.body_md,
                pro_tip: t.pro_tip || null,
                sort_order: Number(t.sort_order) || 0,
                enabled: !!t.enabled,
            };
            if (t.id) {
                await axios.put(`${API}/admin/help/topics/${t.id}`, payload, { withCredentials: true });
                toast.success(`Updated "${t.title}"`);
            } else {
                await axios.post(`${API}/admin/help/topics`, payload, { withCredentials: true });
                toast.success(`Created "${t.title}"`);
            }
            setEditing(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
        finally { setSaving(false); }
    };

    const remove = async (t) => {
        if (!window.confirm(`Delete topic "${t.title}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/admin/help/topics/${t.id}`, { withCredentials: true });
            toast.success('Topic deleted');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    };

    const grouped = topics.reduce((acc, t) => {
        (acc[t.category] = acc[t.category] || []).push(t);
        return acc;
    }, {});

    return (
        <div className="space-y-4" data-testid="help-admin-tab">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <LifeBuoy className="w-5 h-5 text-emerald-400" />
                    Help Center Topics
                    <span className="text-xs text-zinc-500 font-normal">({topics.length})</span>
                </h3>
                <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={load} disabled={loading}
                        className="border-white/20 text-white h-8 text-[11px]"
                        data-testid="help-admin-refresh">
                        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
                    </Button>
                    <Button size="sm" onClick={() => setEditing(blankTopic())}
                        className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold h-8 text-[11px]"
                        data-testid="help-admin-new">
                        <Plus className="w-3.5 h-3.5 mr-1" />New Topic
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-10 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
            ) : topics.length === 0 ? (
                <div className="text-center py-10 text-zinc-500 text-sm">No topics yet — click &quot;New Topic&quot; to add one.</div>
            ) : (
                Object.entries(grouped).map(([cat, items]) => (
                    <div key={cat} className="card-dark p-4">
                        <h4 className="text-[11px] uppercase tracking-widest font-bold text-emerald-300 mb-3">{cat}</h4>
                        <div className="divide-y divide-white/5">
                            {items.map((t, i) => (
                                <div key={t.id} className="py-2.5 flex items-center gap-3" data-testid={`help-admin-row-${t.slug}`}>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-white font-bold text-sm truncate flex items-center gap-2">
                                            {t.title}
                                            {!t.enabled && <span className="text-[9px] text-rose-300 bg-rose-500/10 border border-rose-500/30 px-1 rounded">hidden</span>}
                                        </p>
                                        <p className="text-[11px] text-zinc-500 font-mono truncate">/help#{t.slug}</p>
                                    </div>
                                    <Button size="sm" variant="ghost"
                                        onClick={() => setEditing({ ...t })}
                                        className="h-8 w-8 p-0 text-zinc-400 hover:text-white hover:bg-white/5"
                                        data-testid={`help-admin-edit-${t.slug}`}>
                                        <Edit3 className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button size="sm" variant="ghost"
                                        onClick={() => remove(t)}
                                        className="h-8 w-8 p-0 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                                        data-testid={`help-admin-delete-${t.slug}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                ))
            )}

            {/* Edit / Create dialog */}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl text-zinc-100 max-h-[90vh] overflow-y-auto" data-testid="help-admin-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white">{editing?.id ? 'Edit topic' : 'New topic'}</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Topics use a kebab-case slug for deep linking (e.g. <code className="text-emerald-300">/help#combo-bets</code>).
                            Body supports basic markdown: <code className="text-zinc-300">**bold**</code> and <code className="text-zinc-300">• bullets</code>.
                        </DialogDescription>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div className="sm:col-span-2">
                                    <Label className="text-[11px] text-zinc-400">Title</Label>
                                    <Input value={editing.title}
                                        onChange={(e) => setEditing(p => ({ ...p, title: e.target.value, slug: p.slug || slugify(e.target.value) }))}
                                        className="mt-1 bg-zinc-950 border-zinc-800 text-white"
                                        data-testid="help-admin-field-title" />
                                </div>
                                <div>
                                    <Label className="text-[11px] text-zinc-400">Category</Label>
                                    <select value={editing.category}
                                        onChange={(e) => setEditing(p => ({ ...p, category: e.target.value }))}
                                        className="w-full mt-1 bg-zinc-950 border border-zinc-800 text-white h-9 px-2 rounded text-sm">
                                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div className="sm:col-span-2">
                                    <Label className="text-[11px] text-zinc-400">Slug (kebab-case)</Label>
                                    <Input value={editing.slug}
                                        onChange={(e) => setEditing(p => ({ ...p, slug: slugify(e.target.value) }))}
                                        placeholder="e.g. combo-bets"
                                        className="mt-1 bg-zinc-950 border-zinc-800 text-white font-mono text-sm"
                                        data-testid="help-admin-field-slug" />
                                </div>
                                <div>
                                    <Label className="text-[11px] text-zinc-400">Icon (lucide name)</Label>
                                    <Input value={editing.icon || ''}
                                        onChange={(e) => setEditing(p => ({ ...p, icon: e.target.value }))}
                                        placeholder="HelpCircle"
                                        className="mt-1 bg-zinc-950 border-zinc-800 text-white font-mono text-sm" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-[11px] text-zinc-400">Short summary (one sentence, shown collapsed)</Label>
                                <Input value={editing.short}
                                    onChange={(e) => setEditing(p => ({ ...p, short: e.target.value }))}
                                    className="mt-1 bg-zinc-950 border-zinc-800 text-white"
                                    data-testid="help-admin-field-short" />
                            </div>
                            <div>
                                <Label className="text-[11px] text-zinc-400">Body (markdown)</Label>
                                <textarea value={editing.body_md}
                                    onChange={(e) => setEditing(p => ({ ...p, body_md: e.target.value }))}
                                    rows={10}
                                    className="w-full mt-1 bg-zinc-950 border border-zinc-800 text-white text-sm rounded p-2 font-mono leading-relaxed"
                                    data-testid="help-admin-field-body" />
                            </div>
                            <div>
                                <Label className="text-[11px] text-zinc-400">Pro tip (optional)</Label>
                                <textarea value={editing.pro_tip || ''}
                                    onChange={(e) => setEditing(p => ({ ...p, pro_tip: e.target.value }))}
                                    rows={2}
                                    className="w-full mt-1 bg-zinc-950 border border-zinc-800 text-white text-sm rounded p-2" />
                            </div>
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="flex items-center gap-2">
                                    <Label className="text-[11px] text-zinc-400">Sort order</Label>
                                    <Input type="number" min="0" step="1" value={editing.sort_order || 0}
                                        onChange={(e) => setEditing(p => ({ ...p, sort_order: Number(e.target.value) || 0 }))}
                                        className="w-20 bg-zinc-950 border-zinc-800 text-white h-8 text-sm" />
                                </div>
                                <div className="flex items-center gap-2">
                                    <Switch checked={!!editing.enabled}
                                        onCheckedChange={(v) => setEditing(p => ({ ...p, enabled: v }))} />
                                    <span className="text-xs text-white">{editing.enabled ? 'Visible to players' : 'Hidden'}</span>
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}
                            className="border-white/10 text-white">Cancel</Button>
                        <Button onClick={save} disabled={saving}
                            className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold"
                            data-testid="help-admin-save">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save topic'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default HelpAdminTab;
